"use client";

// Les projets en cours, vus de l'entreprise, du plus pressé au moins pressé. Chaque ligne : le compte à rebours de la
// remise (J−53), l'objet et l'acheteur, la pipeline montée comme un mur (une brique par jalon, posée quand il est
// atteint) avec, dessous, l'activité et la sous-activité en cours ; à droite, qui a la main : l'agent qui avance, ou
// ce qui vous attend (go / no-go, arbitrages, tâches, chapitres à valider). Tout suit l'API en direct (Accueil.tsx) ;
// l'orchestrateur (app/objectif.py) mène chaque projet jusqu'au mémoire prêt.

import Link from "next/link";
import type { CSSProperties } from "react";
import {
  type AffaireResume,
  type EtatObjectif,
  type Fil,
  type GrandeEtape,
} from "@/lib/api";
import { dateLongue, joursAvant } from "@/lib/dates";
import { DemanderAgent } from "./DemanderAgent";
import { Grue } from "./Orchestrateur";
import { Trait } from "./Squelette";
import "./pipeline.css";
import "./projet-ligne.css";

// Les arbitrages en attente d'un projet ; 0 tant que l'API ne les donne pas.
export const aTrancher = (p: AffaireResume) => p.a_trancher ?? 0;

// Les projets, du plus pressé au moins pressé ; ceux dont la remise est passée à la fin.
export function trierProjets(projets: AffaireResume[]): AffaireResume[] {
  const cle = (p: AffaireResume) => {
    const j = joursAvant(p.date_limite);
    return j === null ? Infinity : j < 0 ? 100000 - j : j;
  };
  return [...projets].sort((a, b) => cle(a) - cle(b));
}

export function Projets({
  projets,
  fils,
  objectifs,
  nouveaux = [],
  onDemander,
  onNouveau,
  onRelancer,
  onPause,
  orchestrateurActif,
}: {
  projets: AffaireResume[];
  fils: Record<string, Fil[]>; // les fils de chaque projet
  objectifs: Record<string, EtatObjectif> | null; // où en est l'orchestrateur sur chaque projet
  nouveaux?: string[]; // les projets qu'on vient de prendre dans la Marketplace : leur ligne s'éclaire un moment
  onDemander: (texte: string) => void;
  onNouveau: () => void; // un projet naît d'un appel d'offres qu'on prend dans la Marketplace : on y va
  onRelancer: (affaire?: string) => Promise<void>;
  onPause: (affaire: string, pause: boolean) => Promise<void>;
  orchestrateurActif: boolean; // l'orchestrateur n'est pas en pause
}) {
  const nouveau = (
    <button
      type="button"
      className="bouton nouveau-projet"
      onClick={onNouveau}
      title="Choisir un appel d'offres dans la Marketplace"
    >
      <span aria-hidden="true">+</span> Nouveau projet
    </button>
  );
  if (projets.length === 0) {
    return (
      <div className="vide">
        <p>Aucun projet en cours.</p>
        <p className="discret">
          Un projet naît d&apos;un appel d&apos;offres pris dans la Marketplace.
        </p>
        {nouveau}
      </div>
    );
  }
  return (
    <section className="vue projets">
      <header className="projets-entete">
        <h2>Projets en cours</h2>
        <DemanderAgent
          onDemander={() =>
            onDemander(
              "Fais le point sur les projets en cours : échéance de remise, avancement du mémoire, analyse faite ou non. Dis-moi ce qui presse.",
            )
          }
        >
          Demander un point à l&apos;agent
        </DemanderAgent>
        {nouveau}
      </header>
      <ol className="projets-liste">
        {trierProjets(projets).map((p) => (
          <li key={p.id}>
            <LigneProjet
              projet={p}
              fils={fils[p.id] ?? []}
              objectif={objectifs?.[p.id] ?? null}
              nouveau={nouveaux.includes(p.id)}
              onRelancer={onRelancer}
              onPause={onPause}
              actif={orchestrateurActif}
            />
          </li>
        ))}
      </ol>
    </section>
  );
}

function LigneProjet({
  projet: p,
  fils,
  objectif,
  nouveau,
  onRelancer,
  onPause,
  actif,
}: {
  projet: AffaireResume;
  fils: Fil[];
  objectif: EtatObjectif | null;
  nouveau: boolean;
  onRelancer: (affaire?: string) => Promise<void>;
  onPause: (affaire: string, pause: boolean) => Promise<void>;
  actif: boolean;
}) {
  // Un agent y travaille : la mission de l'orchestrateur, ou un fil du projet qui répond.
  const auTravail = objectif?.etat === "en-cours" || fils.some((f) => f.occupe);
  return (
    <article
      className={`lp${nouveau ? " nouveau" : ""}${auTravail ? " au-travail" : ""}`}
    >
      <Remise date={p.date_limite} />
      <div className="lp-corps">
        <h3 className="lp-titre">
          <Link href={`/projets/${p.id}`} className="lp-lien">
            {p.objet}
          </Link>
        </h3>
        <p className="lp-acheteur" title={`Référence ${p.reference}`}>
          {p.acheteur}
        </p>
        <Mur projet={p} auTravail={auTravail} />
      </div>
      <Main
        projet={p}
        objectif={objectif}
        fils={fils}
        actif={actif}
        onRelancer={onRelancer}
        onPause={onPause}
      />
    </article>
  );
}

// Le compte à rebours, comme sur un planning de chantier : J−53, Jour J, J+2 une fois la remise passée.
function Remise({ date }: { date: string }) {
  const j = joursAvant(date);
  const urgence = j === null ? "" : j < 0 ? "passee" : j <= 7 ? "proche" : "";
  return (
    <div className={`lp-remise ${urgence}`}>
      <span className="lp-j">
        {j === null ? "—" : j === 0 ? "Jour J" : j > 0 ? `J−${j}` : `J+${-j}`}
      </span>
      <span className="lp-date">
        {j !== null && j < 0 ? "remise passée" : `remise ${dateLongue(date)}`}
      </span>
    </div>
  );
}

// Qui fait une activité, dit comme on le dit : « vous et l'agent ».
const PERSONNES: Record<string, string> = {
  entreprise: "vous",
  agent: "l'agent",
  acheteur: "l'acheteur",
};
const qui = (acteurs: string[]) => {
  const noms = acteurs.map((a) => PERSONNES[a] ?? a);
  return noms.length > 1
    ? `${noms.slice(0, -1).join(", ")} et ${noms[noms.length - 1]}`
    : (noms[0] ?? "");
};

// La pipeline en mur : une assise par grande étape, large de ses jalons ; chaque jalon est une brique, posée quand il
// est atteint. Les briques d'une étape montent en deux rangs à joints croisés, le rang du bas d'abord. La brique
// suivante de l'étape en cours est à la grue : elle descend en place quand un agent travaille.
function Mur({
  projet: p,
  auTravail,
}: {
  projet: AffaireResume;
  auTravail: boolean;
}) {
  const etapes = p.avancement?.etapes ?? [];
  if (etapes.length === 0) return null;
  const total = etapes.reduce((n, e) => n + Math.max(e.total, 1), 0);
  const ici = etapes.findIndex((e) => e.etat === "en-cours");
  const avant = etapes
    .slice(0, Math.max(ici, 0))
    .reduce((n, e) => n + Math.max(e.total, 1), 0);
  const chemin = p.avancement.en_cours ?? [];
  const [etape, ...fines] = chemin;
  const actuelle = fines[fines.length - 1] ?? etape;
  const parents = fines.slice(0, -1);
  const fini = etapes.every((e) => e.etat === "fait");

  return (
    <Link
      href={`/projets/${p.id}`}
      className="lp-mur-lien"
      aria-label={`Pipeline : ${p.avancement.faits} jalons sur ${p.avancement.total}${actuelle ? `, en cours : ${actuelle.nom}` : ""}`}
    >
      <span className="lp-mur" aria-hidden>
        {etapes.map((e, i) => (
          <Assise key={e.id} etape={e} courante={i === ici} />
        ))}
      </span>
      <span
        className="lp-en-cours"
        style={{ "--x": (avant / total) * 100 } as CSSProperties}
      >
        {fini ? (
          <strong className="lp-activite">Toutes les étapes sont faites</strong>
        ) : actuelle ? (
          <>
            <span className="lp-activite-ligne">
              {auTravail && <Grue etat="au-travail" className="petite" />}
              <strong className="lp-activite">{actuelle.nom}</strong>
              <span className="lp-qui">{qui(actuelle.acteurs)}</span>
            </span>
            {parents.length > 0 && (
              <span className="lp-contexte">
                {parents.map((x) => x.nom).join(", ")}
              </span>
            )}
          </>
        ) : (
          <span className="lp-contexte">{p.avancement.courant ?? ""}</span>
        )}
      </span>
    </Link>
  );
}

function Assise({
  etape: e,
  courante,
}: {
  etape: GrandeEtape;
  courante: boolean;
}) {
  const jalons =
    e.jalons ??
    Array.from({ length: e.total }, (_, k) => ({ nom: "", fait: k < e.faits }));
  const prochaine = courante ? jalons.findIndex((j) => !j.fait) : -1;
  // Le rang du bas d'abord : on monte un mur par le pied. L'assise est une grille de demi-briques ; chaque brique en
  // couvre deux, et le rang du haut part d'une demi-brique plus loin. Sa dernière brique, si elle dépasse, est coupée.
  const bas = Math.max(Math.ceil(jalons.length / 2), 1);
  return (
    <span
      className={`lp-assise ${e.etat}`}
      style={{
        flexGrow: Math.max(e.total, 1),
        gridTemplateColumns: `repeat(${bas * 2}, minmax(0, 1fr))`,
      }}
      title={`${e.nom} : ${e.faits} jalon${e.faits > 1 ? "s" : ""} sur ${e.total}`}
    >
      {jalons.map((j, k) => {
        const enHaut = k >= bas;
        const debut = enHaut ? 2 + 2 * (k - bas) : 1 + 2 * k;
        return (
          <i
            key={k}
            className={j.fait ? "posee" : k === prochaine ? "a-la-grue" : ""}
            style={{
              gridRow: enHaut ? 1 : 2,
              gridColumn: `${debut} / span ${debut + 2 > bas * 2 + 1 ? 1 : 2}`,
            }}
            title={j.nom ? `${j.nom}${j.fait ? " (atteint)" : ""}` : undefined}
          />
        );
      })}
    </span>
  );
}

const pluriel = (n: number, mot: string) => `${n} ${mot}${n > 1 ? "s" : ""}`;

// Qui a la main sur le projet : l'agent qui avance, ou ce que l'entreprise doit faire pour qu'il reparte.
function Main({
  projet: p,
  objectif: o,
  fils,
  actif,
  onRelancer,
  onPause,
}: {
  projet: AffaireResume;
  objectif: EtatObjectif | null;
  fils: Fil[];
  actif: boolean;
  onRelancer: (affaire?: string) => Promise<void>;
  onPause: (affaire: string, pause: boolean) => Promise<void>;
}) {
  const arbitrages = o?.arbitrages.length ?? aTrancher(p);
  const critiques =
    o?.arbitrages.filter((a) => a.gravite === "critique").length ?? 0;
  const taches = o?.taches ?? [];
  const nTaches = o ? taches.length : (p.a_faire ?? 0);
  const goNoGo = o?.decision === "a-prendre"; // la grille remplie, la suite attend la direction
  const aValider = o && o.mission === null ? o.a_valider : 0;
  const occupe = fils.find((f) => f.occupe);

  const pause = o &&
    o.etat !== "pret" &&
    o.etat !== "en-pause" &&
    o.etat !== "no-go" && (
      <button
        type="button"
        className="lp-pause"
        onClick={() => void onPause(p.id, true)}
        title="Le Conducteur laisse ce projet : ni réveil, ni décision ne le relancent (le tour en cours finit)"
      >
        Mettre en pause
      </button>
    );

  if (o?.etat === "pret")
    return (
      <aside className="lp-main pret">
        <strong className="lp-main-titre">Mémoire prêt</strong>
        <span className="lp-main-texte">
          Il reste à le relire et à déposer l&apos;offre.
        </span>
      </aside>
    );
  if (o?.etat === "no-go")
    return (
      <aside className="lp-main no-go">
        <strong className="lp-main-titre">No-go</strong>
        <span className="lp-main-texte">
          La direction a décidé de ne pas répondre.
        </span>
        <Link href={`/projets/${p.id}?onglet=decision`} className="lp-item">
          Revoir la décision
        </Link>
      </aside>
    );
  if (o?.etat === "en-pause")
    return (
      <aside className="lp-main en-pause">
        <strong className="lp-main-titre">En pause</strong>
        <span className="lp-main-texte">Le Conducteur laisse ce projet.</span>
        <button
          type="button"
          className="bouton-petit"
          onClick={() => void onPause(p.id, false)}
        >
          Reprendre
        </button>
      </aside>
    );
  if (o?.etat === "en-cours" || occupe)
    return (
      <aside className="lp-main agent">
        <strong className="lp-main-titre">L&apos;agent avance</strong>
        <span className="lp-main-texte">
          {o?.mission?.nom ?? occupe?.titre}
        </span>
        {pause}
      </aside>
    );

  const items = [
    goNoGo && (
      <Link
        key="go"
        href={`/projets/${p.id}?onglet=decision`}
        className="lp-item go"
      >
        Décider go / no-go
      </Link>
    ),
    arbitrages > 0 && (
      <Link
        key="arb"
        href={`/projets/${p.id}?onglet=arbitrages`}
        className={`lp-item arbitrages${critiques ? " critique" : ""}`}
        title={
          critiques
            ? `dont ${critiques} critique${critiques > 1 ? "s" : ""}`
            : undefined
        }
      >
        <b>{arbitrages}</b> {arbitrages > 1 ? "arbitrages" : "arbitrage"} à
        trancher
      </Link>
    ),
    nTaches > 0 && (
      <span
        key="taches"
        className="lp-item taches"
        title={taches.map((t) => `• ${t.titre}`).join("\n") || undefined}
      >
        <b>{nTaches}</b> {nTaches > 1 ? "pièces" : "pièce"} à fournir
      </span>
    ),
    aValider > 0 && (
      <Link
        key="valider"
        href={`/projets/${p.id}?onglet=memoire`}
        className="lp-item valider"
      >
        <b>{aValider}</b> {aValider > 1 ? "chapitres" : "chapitre"} à valider
      </Link>
    ),
  ].filter(Boolean);

  if (items.length > 0)
    return (
      <aside className="lp-main a-vous">
        <strong className="lp-main-titre">À vous</strong>
        <div className="lp-items">{items}</div>
        {pause}
      </aside>
    );

  // Rien n'attend l'entreprise : l'agent a calé, ou l'orchestrateur le reprendra.
  const relancer = actif && (
    <button
      type="button"
      className="bouton-petit"
      onClick={() => void onRelancer(p.id)}
    >
      {o?.etat === "bloque" ? "Relancer" : "Lancer"}
    </button>
  );
  if (o?.etat === "bloque")
    return (
      <aside className="lp-main cale">
        <strong className="lp-main-titre">L&apos;agent a calé</strong>
        <span className="lp-main-texte">{o.raison}</span>
        {relancer}
      </aside>
    );
  return (
    <aside className="lp-main">
      <span className="lp-main-texte">
        {o
          ? "Le Conducteur le reprendra à son réveil."
          : pluriel(fils.length, "conversation")}
      </span>
      {o && relancer}
    </aside>
  );
}

// La liste des projets pendant son chargement : l'en-tête, puis trois lignes à la forme d'un projet (le compte à
// rebours, le titre, l'acheteur et le mur ; à droite, qui a la main).
export function SqueletteProjets() {
  return (
    <section
      className="vue projets squelette"
      aria-busy="true"
      aria-label="Chargement des projets"
    >
      <header className="projets-entete">
        <Trait className="trait-h2" largeur={30} />
      </header>
      <ol className="projets-liste">
        {[72, 58, 66].map((l, k) => (
          <li key={k}>
            <div className="lp">
              <div className="lp-remise">
                <Trait className="trait-gras" largeur={60} i={k * 5} />
                <Trait className="trait-petit" largeur={85} i={k * 5 + 1} />
              </div>
              <div className="lp-corps">
                <Trait className="trait-gras" largeur={l} i={k * 5 + 2} />
                <Trait className="trait-petit" largeur={l - 24} i={k * 5 + 3} />
                <Trait largeur="min(520px, 100%)" i={k * 5 + 4} />
              </div>
              <div className="lp-main">
                <Trait className="trait-petit" largeur={80} i={k * 5 + 4} />
              </div>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
